import { MenuIcon } from "lucide-react";
import { ReactNode } from "react";
import { CustomLabels, HeaderBreadcrumbs } from "./HeaderBreadcrumbs";
import { usePreload } from "./PreloadProvider";
import { useScrollRestoration } from "./ScrollRestoration";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

interface IProps {
    customLabels?: CustomLabels;
    breadcrumbReplacement?: ReactNode;
    noSpacing?: boolean;
    mainClassName?: string;
    className?: string;
    children: ReactNode;
}

export const PageFrame = ({
    customLabels,
    breadcrumbReplacement,
    noSpacing,
    className,
    mainClassName,
    children
}: IProps) => {
    const { isPreloading } = usePreload();

    useScrollRestoration({ containerId: "root", root: true });
    const { ref, onScroll } = useScrollRestoration({
        containerId: "PageFrame"
    });

    return (
        <div className="flex grow flex-col md-not-print:absolute md-not-print:inset-0">
            <div className="sticky top-0 z-10 w-full bg-background pb-2 md:static md:pb-4 print:hidden">
                <header className="flex gap-1 pl-2 pr-4 pt-1 md:pl-4 md:pt-4">
                    {!isPreloading && <ToggleSidebarButton />}
                    <div className="pt-2 md:pt-0">
                        {breadcrumbReplacement ?? (
                            <HeaderBreadcrumbs customLabels={customLabels} />
                        )}
                    </div>
                </header>
            </div>
            <main
                className={cn(
                    "relative z-0 grow md-not-print:overflow-auto",
                    mainClassName
                )}
                ref={ref}
                onScroll={onScroll}
            >
                <div
                    className={cn(
                        "mx-auto max-w-4xl pb-6 pt-2 md-not-print:px-4",
                        !noSpacing && "space-y-6",
                        className
                    )}
                >
                    {children}
                </div>
            </main>
        </div>
    );
};

const ToggleSidebarButton = () => {
    const { toggleSidebar } = useSidebar();

    return (
        <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="flex h-7 items-end pb-1 md:hidden"
        >
            <MenuIcon />
            <span className="sr-only">Toggle sidebar</span>
        </Button>
    );
};
